import HourglassSVG from "../components/HourglassSVG";
import VehicleTypes from "../types/vehicleTypes";

interface GradeDetailsType {
  color: string;
  icon: JSX.Element | string;
  label: string;
  text: string;
}

interface GradesDetailsType {
  [key: number]: GradeDetailsType;
}

export const generalGradesDetails: GradesDetailsType = {
  0: {
    color: "#bdbec0",
    icon: <HourglassSVG maxWidth={[10, 11]} height={[13, 14]}></HourglassSVG>,
    label: "Unknown",
    text: "The vehicle has not been appraised. If an appraisal is due, the vehicle details and images will be updated once the appraisal is completed.",
  },
  1: {
    color: "#009d45",
    icon: "1",
    label: "Grade",
    text:
      "\u2022 Vehicle panels may have minor defects including dents up to 30mm, scratches up to 25mm \n" +
      "\u2022 Chips up to 10mm on glass\n" +
      "\u2022 Bumpers may have areas of scratches up to 100mm \n" +
      "\u2022 Wheels can be affected with scratches or corrosion\n" +
      "\u2022 Interior may have minor scuffing due to wear & tear and replacement of minor trim items may also be required",
  },
  2: {
    color: "#00c546",
    icon: "2",
    label: "Grade",
    text:
      "The vehicle may have numerous defects as in Grade 1 plus 1 of the following:\n" +
      "\u2022 Paint defect larger than 25mm on a panel or 100mm on a bumper\n" +
      "\u2022 Dent larger than 30mm\n" +
      "\u2022 Defect to glass larger than 10mm\n" +
      "\u2022 Significant trim / part could also be required",
  },
  3: {
    color: "#a7dc26",
    icon: "3",
    label: "Grade",
    text:
      "The vehicle may have defects as in Grade 1 & 2 plus it could also include or have a combination of:\n" +
      "\u2022 Up to 5 panels with paint defects larger than 25mm or 100mm on a bumper \n" +
      "\u2022 Up to 3 panels / bumpers affected with dents larger than 30mm\n" +
      "\u2022 Significant trims or parts could also be required",
  },
  4: {
    color: "#fcc800",
    icon: "4",
    label: "Grade",
    text:
      "The vehicle may have defects as in Grade 1, 2 & 3 it could also include or have a combination of:\n" +
      "\u2022 A panel with significant damage i.e. over 30% or a significant crack to a bumper\n" +
      "\u2022 Up to 10 panels with paint defects larger than 25mm or 100mm on a bumper \n" +
      "\u2022 Up to 7 panels / bumpers affected with dents larger than 30mm\n" +
      "\u2022 Multiple trims or parts could also be required",
  },
  5: {
    color: "#f56600",
    icon: "5",
    label: "Grade",
    text:
      "The vehicle may have defects as in Grade 1, 2, 3 & 4 it could also include:\n" +
      "\u2022 More than 2 panels / bumpers with significant cracks or damage i.e. over 30% of the panel\n" +
      "\u2022 8 or more panels / bumpers affected with dents larger than 30mm\n" +
      "\u2022 11 panels or more with paint defects larger than 25mm or 100mm on a bumper \n" +
      "\u2022 Up to 1 structural panel with significant damage i.e. over 30% of the panel \n" +
      "\u2022 Significant tears, holes or cuts in convertible roofs",
  },
  6: {
    color: "primary-navy-blue",
    icon: "R",
    label: "Retail ready",
    text:
      "Grade R is a new highest condition standard from BCA.\n" +
      "Vehicles are upgraded to a Retail Ready condition (Grade R) allowing you to immediately place these vehicles onto your forecourt with little or no preparation once purchased from BCA.",
  },
  7: {
    color: "lightest-grey",
    icon: "U",
    label: "Unclassified",
    text:
      "Vehicles in this grade could fall into the following categories:\n" +
      "\u2022 Recorded items that exceed the criteria of Grade 5\n" +
      "\u2022 Substantial Accident damage\n" +
      "\u2022 Visible substantial accident damage \n" +
      "\u2022 Major Parts Missing\n" +
      "\u2022 Multiple significant parts missing from either the interior or exterior \n" +
      "\u2022 Multiple unrecorded items\n" +
      "\u2022 Vehicles which are uneconomical to appraise",
  },
};

export const recreationalGradesDetails: GradesDetailsType = {
  0: {
    color: "#bdbec0",
    icon: <HourglassSVG maxWidth={[10, 11]} height={[13, 14]}></HourglassSVG>,
    label: "Unknown",
    text: "The vehicle has not been appraised. If an appraisal is due, the vehicle details and images will be updated once the appraisal is completed.",
  },
  1: {
    color: "#009d45",
    icon: "1",
    label: "Grade",
    text:
      "The vehicle's habitation area may show minor defects, such as scuffs, small drilled holes in the woodwork, and may require minor replacement parts.\n​" +
      "\u2022 Panels may show minor defects, including dents up to 60mm and scratches through the paint up to 30mm.\n" +
      "\u2022 Glass may have chips up to 15mm and light surface scratches may be visible on windows or other glazed areas.\n" +
      "\u2022 Bumpers may have scratches up to 150mm in length.\n" +
      "\u2022 Wheels may exhibit scratches or signs of corrosion.\n" +
      "\u2022 Cabin area may show minor defects, and minor trim replacements may be required.",
  },
  2: {
    color: "#00c546",
    icon: "2",
    label: "Grade",
    text:
      "The vehicle may exhibit defects consistent with those outlined in Grade 1, plus one of the following:\n" +
      "\u2022 Visible signs of water ingress limited to the shower area.\n" +
      "\u2022 A significant habitation part may require replacement.​\n" +
      "\u2022 A paint defect larger than 30mm on an exterior panel or larger than 150mm on a bumper.​\n" +
      "\u2022 A dent larger than 60mm on an exterior panel.​​\n" +
      "\u2022 A chip in the glass larger than 15mm, or the presence of a crack.​\n" +
      "\u2022 Significant exterior trims or parts may require replacement.​",
  },
  3: {
    color: "#a7dc26",
    icon: "3",
    label: "Grade",
    text:
      "The vehicle may exhibit defects consistent with those outlined in Grades 1 and 2, and may also include or combine the following:\n" +
      "\u2022 Visible signs of water ingress affecting up to one panel.\n" +
      "\u2022 Significant habitation parts may require replacement.​\n" +
      "\u2022 Up to five exterior panels may have paint defects larger than 30mm, or larger than 150mm on a bumper.\n" +
      "\u2022 Up to three exterior panels or bumpers may have dents larger than 60mm.​\n" +
      "\u2022 Significant bumper defect, such as a crack over 60mm, may be present.​\n" +
      "\u2022 Significant exterior trims or parts may also require replacement.​",
  },
  4: {
    color: "#fcc800",
    icon: "4",
    label: "Grade",
    text:
      "The vehicle may exhibit defects consistent with those outlined in Grades 1, 2 and 3, and may also include:\n" +
      "\u2022 Visible signs of water ingress affecting up to two panels.\n" +
      "\u2022 Significant defect over 100mm to the pop-top roof fabric.​\n" +
      "\u2022 A panel with substantial damage, such as a dent over 30% of the whole area, or a significant crack or hole.\n" +
      "\u2022 Additional panels may have paint defects larger than 30mm or dents larger than 60mm.​",
  },
  5: {
    color: "#f56600",
    icon: "5",
    label: "Grade",
    text:
      "The vehicle may exhibit defects consistent with those outlined in Grades 1, 2, 3 and 4, and may also include or combine the following:​\n" +
      "\u2022 Visible signs of water ingress affecting three or more panels, or floor delamination.​\n" +
      "\u2022 Up to one structural panel may have significant damage, such as a dent affecting over 30% of its area, or a significant crack or hole.​",
  },
  6: {
    color: "primary-navy-blue",
    icon: "R",
    label: "Retail ready",
    text:
      "Grade R is a new highest condition standard from BCA.\n" +
      "Vehicles are upgraded to a Retail Ready condition (Grade R) allowing you to immediately place these vehicles onto your forecourt with little or no preparation once purchased from BCA.",
  },
  7: {
    color: "lightest-grey",
    icon: "U",
    label: "Unclassified",
    text:
      "Vehicles in this grade may fall into one of the following categories:​\n" +
      "\u2022 Recorded defects that exceed the thresholds outlined in Grade 5.\n" +
      "\u2022 Visible and substantial accident damage.​​\n" +
      "\u2022 Fire damaged.\n" +
      "\u2022 Multiple significant parts missing from either the interior or exterior.​",
  },
};

const recreationalTypes = ["Caravan", "Motorhome"];

export default function getGradesDetails(
  vehicleType: VehicleTypes
): GradesDetailsType {
  if (recreationalTypes.includes(vehicleType)) {
    return recreationalGradesDetails;
  }

  return generalGradesDetails;
}
